import qs from 'qs';
import {
    INTERNAL_SERVER_ERROR_HTTP_CODE,
    UNAUTHORIZED_HTTP_CODE,
    UNAUTHORIZED_MESSAGE,
    UNEXPECTED_ERROR_TRY_LATER,
    FAILED_TO_FETCH_ERROR,
} from 'constants/errors';
import { IHttpClientRepository, IRequestOptions } from './interfaces';

export default class HttpClientRepository implements IHttpClientRepository {
    public apiUrl: string;

    public headers: Record<string, string>;

    constructor() {
        this.apiUrl = 'https://agbp.betgames.local';

        this.headers = {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json;charset=UTF-8',
        };
    }

    private formatUrl = <P = unknown>(url: string, params: P, local: boolean): string => {
        let requestURL = local ? url : `${this.apiUrl}${url}`;

        if (params) {
            // requestURL += `?${qs.stringify(snakeCaseKeys(requestObject.params))}`;
            requestURL += qs.stringify(params, {
                encode: false,
                skipNulls: true,
                addQueryPrefix: true,
            });
        }

        return requestURL;
    };

    private makeRequest = <T, P>(url: string, requestOptions: IRequestOptions<P>): Promise<T> => {
        const requestURL = this.formatUrl(url, requestOptions.params, requestOptions.local);

        return fetch(requestURL, {
            ...requestOptions.options,
            signal: requestOptions.signal,
            headers: {
                ...this.headers,
                ...requestOptions.options?.headers,
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }

                if (response.status === UNAUTHORIZED_HTTP_CODE) {
                    return response
                        .json()
                        .then((res) => {
                            if (res.integration) {
                                return Promise.reject(res);
                            }

                            throw Error(UNAUTHORIZED_MESSAGE);
                        })
                        .catch((error) => {
                            if (error.integration) {
                                return error;
                            }

                            throw Error(UNAUTHORIZED_MESSAGE);
                        });
                }
                if (response.status === INTERNAL_SERVER_ERROR_HTTP_CODE) {
                    throw Error(UNEXPECTED_ERROR_TRY_LATER);
                }

                return response.json().then((errorData) => {
                    if (typeof errorData === 'string') {
                        throw Error(errorData);
                    }
                    if (errorData.message) {
                        throw Error(errorData.message);
                    }
                    throw Error(UNEXPECTED_ERROR_TRY_LATER);
                });
            })
            .catch((error) => {
                if (error instanceof Error) {
                    if (error.message === FAILED_TO_FETCH_ERROR) {
                        throw new Error(UNEXPECTED_ERROR_TRY_LATER);
                    }
                    throw error;
                }

                throw error.response ? error.response : { message: 'Request error' };
            });
    };

    public setApiUrl = (apiUrl: string): void => {
        this.apiUrl = apiUrl;
    };

    public get = <T = unknown, P = unknown>(
        url: string,
        requestOptions: IRequestOptions<P> = {},
    ): Promise<T> =>
        this.makeRequest<T, P>(url, {
            ...requestOptions,
            options: {
                ...requestOptions.options,
                method: 'GET',
            },
        });

    public post = <T = unknown, P = unknown>(
        url: string,
        requestObject: IRequestOptions<P> = {},
    ): Promise<T> => {
        const transformed = requestObject.data;

        return this.makeRequest<T, P>(url, {
            ...requestObject,
            options: {
                ...requestObject.options,
                method: 'POST',
                body: JSON.stringify(transformed),
            },
        });
    };

    public remove = <T = unknown, P = Record<string, unknown>>(
        url: string,
        requestOptions: IRequestOptions<P> = {},
    ): Promise<T> =>
        this.makeRequest<T, P>(url, {
            ...requestOptions,
            options: {
                ...requestOptions.options,
                method: 'DELETE',
            },
        });
}
